import type { PlanTier } from '@dashboard/stores/subscriptionStore'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import { computed } from 'vue'

/**
 * Feature-to-minimum-tier mapping — used ONLY for UI labels ("requires Scale plan").
 * Access decisions use the backend-supplied plan.features array, not this map.
 *
 * KEEP IN SYNC with config/plans.php tiers.*.features
 * If you add or move a feature in plans.php, update this map too.
 */
const FEATURE_TIER_MAP: Record<string, PlanTier> = {
  // Grow features (available to all tiers)
  flows: 'grow',
  questions: 'grow',
  segments: 'grow',
  signals: 'grow',
  widgets: 'grow',
  offers: 'grow',
  forms: 'grow',
  phrases: 'grow',
  basic_analytics: 'grow',
  lead_alerts: 'grow',
  mcp_access: 'grow',
  // Scale features
  campaigns: 'scale',
  api_access: 'scale',
  full_analytics: 'scale',
  goals: 'scale',
  personalized_emails: 'scale',
  // Pro features
  account_intelligence: 'pro',
  people_pages: 'pro',
  webhooks: 'pro',
  split_testing: 'pro'
}

export function useFeatureGating() {
  const subscriptionStore = useSubscriptionStore()

  /** Check if the user can access a specific feature */
  const canAccessFeature = (feature: string): boolean => subscriptionStore.hasFeature(feature)

  /** Get the minimum tier required for a feature */
  const requiredTierForFeature = (feature: string): PlanTier => FEATURE_TIER_MAP[feature] ?? 'grow'

  /** Get the display name for a tier */
  const tierDisplayName = (tier: PlanTier): string => {
    const names: Record<PlanTier, string> = {
      grow: 'Grow',
      scale: 'Scale',
      pro: 'Pro',
      legacy: 'Legacy',
      legacy_cta: 'Legacy CTA'
    }
    return names[tier] ?? tier
  }

  /** Feature locks follow the same backend feature list as access decisions. */
  const isFeatureLocked = (feature: string): boolean => !canAccessFeature(feature)

  /** Get upgrade message for a locked feature */
  const upgradeMessage = (feature: string): string => {
    const tier = requiredTierForFeature(feature)
    return `Upgrade to ${tierDisplayName(tier)} to unlock this feature.`
  }

  return {
    canAccessFeature,
    requiredTierForFeature,
    tierDisplayName,
    isFeatureLocked,
    upgradeMessage,
    planTier: computed(() => subscriptionStore.planTier),
    isLegacy: computed(() => subscriptionStore.isLegacy)
  }
}
